import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, User, Key, Lock, Save, CheckCircle, AlertCircle, LogIn, Mail, UserPlus } from "lucide-react";
import HeaderMobile from "./HeaderMobile";
import Header from "./Header.jsx";
import useMediaQuery from "../hooks/useMediaQuery";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import axios from "./AxiosInstance.jsx";

const apiUrl = process.env.REACT_APP_API_URL;

function LoginPage({ refreshGameState, gameState }) {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [currentStep, setCurrentStep] = useState("auth"); // "auth", "apiKey"
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    apiKey: ""
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    old_password: "",
    new_password: ""
  });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [showMenu, setShowMenu] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);

  const newGame = async () => {
    try {
      const response = await axios.get("/new_game");
      if (response.status !== 200) {
        throw new Error("Erreur lors de la création de la nouvelle partie");
      }

      await refreshGameState();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate("/accueil");
    } catch (error) {
      console.error("Erreur lors de la requête new_game :", error);
      alert("Impossible de lancer la partie. Veuillez réessayer.");
    }
  };

  useEffect(() => {
    checkExistingToken();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: "", message: "" }), 4000);
  };

  const retourGame = () => {
    window.location.href = "/game";
  };

  const checkExistingToken = async () => {
    const token = localStorage.getItem("jwtToken");
    if (!token) return;

    try {
      const response = await fetch(apiUrl + "/api/verify-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        setAuthenticatedUser(result.username);
        if (result.hasApiKey) {
          window.location.href = "/game";
        } else {
          setCurrentStep("apiKey");
          setFormData((prev) => ({ ...prev, username: result.username }));
        }
      } else {
        localStorage.removeItem("jwtToken");
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du token:", error);
      localStorage.removeItem("jwtToken");
      resetForm();
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordChangeData.old_password || !passwordChangeData.new_password) {
      showNotification("error", "Les deux mots de passe sont requis");
      return;
    }

    const token = localStorage.getItem("jwtToken");
    if (!token) {
      showNotification("error", "Vous devez être connecté");
      return;
    }

    try {
      const response = await fetch(apiUrl + "/api/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(passwordChangeData)
      });

      const result = await response.json();
      if (response.ok) {
        showNotification("success", result.message || "Mot de passe mis à jour !");
        setPasswordChangeData({ old_password: "", new_password: "" });
        setShowPasswordChange(false);
      } else {
        showNotification("error", result.message || "Erreur lors du changement de mot de passe");
      }
    } catch (error) {
      console.error("Erreur API:", error);
      showNotification("error", "Erreur de connexion au serveur");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    setAuthenticatedUser(null);
    setCurrentStep("auth");
    resetForm();
    showNotification("success", "Déconnexion réussie !");
  };

  const handleLogin = async () => {
    setIsLoading(true);

    try {
      const token = await executeRecaptcha("login");
      if (!token) {
        showNotification("error", "Échec de l'exécution du captcha");
        setIsLoading(false);
        return;
      }

      const response = await fetch(apiUrl + "/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          token: token
        })
      });

      const result = await response.json();

      if (result.status === "ok") {
        localStorage.setItem("jwtToken", result.token);
        setAuthenticatedUser(result.username);

        if (result.hasApiKey) {
          showNotification("success", "Connexion réussie !");
          setTimeout(() => {
            window.location.href = "/game";
          }, 1000);
        } else {
          setCurrentStep("apiKey");
          showNotification("success", "Connexion réussie ! Veuillez configurer votre clé API.");
        }
      } else {
        showNotification("error", result.message || "Erreur de connexion");
      }
    } catch (error) {
      console.error("Erreur de connexion:", error);
      showNotification("error", "Erreur de connexion au serveur");
    } finally {
      setIsLoading(false);
      setCaptchaToken(null);
    }
  };

  const handleRegister = async () => {
    setIsLoading(true);

    try {
      const token = await executeRecaptcha("register");
      if (!token) {
        showNotification("error", "Échec de l'exécution du captcha");
        setIsLoading(false);
        return;
      }

      const response = await fetch(apiUrl + "/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          username: formData.username,
          password: formData.password,
          token: token
        })
      });

      const result = await response.json();

      if (response.ok) {
        localStorage.setItem("jwtToken", result.token);
        setAuthenticatedUser(result.username);
        setCurrentStep("apiKey");
        showNotification("success", "Compte créé avec succès ! Veuillez configurer votre clé API.");
      } else {
        showNotification("error", result.message || "Erreur lors de la création du compte");
      }
    } catch (error) {
      console.error("Erreur de création de compte:", error);
      showNotification("error", "Erreur de connexion au serveur");
    } finally {
      setIsLoading(false);
      setCaptchaToken(null);
    }
  };

  const handleSaveApiKey = async () => {
    if (!formData.apiKey.trim()) {
      showNotification("error", "La clé API est requise");
      return;
    }
    if (!formData.apiKey.startsWith("sk-")) {
      showNotification("error", "La clé API doit commencer par 'sk-'");
      return;
    }

    setIsLoading(true);
    const token = localStorage.getItem("jwtToken");

    try {
      const response = await fetch(apiUrl + "/api/save-api-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ apiKey: formData.apiKey })
      });

      const result = await response.json();

      if (response.ok) {
        showNotification("success", "Clé API configurée avec succès !");
        setTimeout(() => {
          window.location.href = "/game";
        }, 1500);
      } else {
        showNotification("error", result.message || "Erreur lors de l'enregistrement de la clé API");
      }
    } catch (error) {
      console.error("Erreur de sauvegarde:", error);
      showNotification("error", "Erreur de connexion au serveur");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: "", username: "", password: "", apiKey: "" });
    setCurrentStep("auth");
    setAuthenticatedUser(null);
  };

  // ————— Étape de configuration de la clé API —————
  if (currentStep === "apiKey") {
    const apiKeyContent = (
      <div className="w-full max-w-md">
        {notification.message && (
          <div
            className={`mb-6 p-4 rounded-xl border backdrop-blur-sm transition-all duration-300 ${notification.type === "success"
              ? "bg-green-500/10 border-green-500/20 text-green-400"
              : "bg-red-500/10 border-red-500/20 text-red-400"
              }`}
          >
            <div className="flex items-center gap-3">
              {notification.type === "success" ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </div>
        )}

        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8">
          {!showPasswordChange && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Key className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Configuration de la clé API</h1>
                <p className="text-gray-400 text-sm">
                  Bonjour {authenticatedUser}, vous allez enregistrer ou mettre à jour votre clé OpenAI
                </p>
              </div>

              {/* —— Formulaire clé API (corrige le warning) —— */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isLoading) handleSaveApiKey();
                }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Clé API OpenAI
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      id="apiKey"
                      name="apiKey"
                      autoComplete="off"
                      value={formData.apiKey}
                      onChange={(e) => handleInputChange("apiKey", e.target.value)}
                      placeholder="sk-..."
                      className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Obtenez votre clé API sur{" "}
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      platform.openai.com
                    </a>
                  </p>
                </div>

                <div className="mt-2 space-y-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Configuration...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Configurer la clé API
                      </>
                    )}
                  </button>

                  {authenticatedUser && (
                    <>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="absolute top-4 right-4 px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg transition"
                      >
                        Déconnexion
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPasswordChange(!showPasswordChange)}
                        className="w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
                      >
                        {showPasswordChange ? "Annuler le changement de mot de passe" : "Changer le mot de passe"}
                      </button>
                    </>
                  )}
                </div>
              </form>
            </>
          )}

          {showPasswordChange && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Changement de mot de passe</h1>
                <p className="text-gray-400 text-sm">
                  Bonjour {authenticatedUser}, vous pouvez modifier votre mot de passe ici :
                </p>
              </div>

              {/* —— Formulaire changement de mot de passe —— */}
              <form onSubmit={handleChangePassword} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Ancien mot de passe</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={passwordChangeData.old_password}
                    onChange={(e) =>
                      setPasswordChangeData({ ...passwordChangeData, old_password: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    placeholder="Ancien mot de passe"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Nouveau mot de passe</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={passwordChangeData.new_password}
                    onChange={(e) =>
                      setPasswordChangeData({ ...passwordChangeData, new_password: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white"
                    placeholder="Nouveau mot de passe"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition"
                >
                  Mettre à jour le mot de passe
                </button>
              </form>
            </>
          )}

          <button
            onClick={retourGame}
            className="w-full mt-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            Retour
          </button>
        </div>
      </div>
    );

    if (isMobile) {
      return (
        <div className="max-h-screen min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
          <HeaderMobile setShowMenu={setShowMenu} showMenu={showMenu} setShowConfirm={setShowConfirm} />
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">{apiKeyContent}</div>
          {showConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full text-center">
                <h2 className="text-xl font-bold text-red-600 mb-4">Confirmation</h2>
                <p className="text-sm text-gray-700 mb-6">
                  Cette action est irréversible et supprimera la partie en cours. Voulez-vous vraiment commencer une
                  nouvelle partie ?
                </p>
                <div className="flex justify-center space-x-4">
                  <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded bg-gray-500 hover:bg-gray-300">
                    Annuler
                  </button>
                  <button onClick={newGame} disabled={isLoading} className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                    {isLoading ? "Chargement..." : "Confirmer"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <Header gameState={gameState} refreshGameState={refreshGameState} />
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
          {apiKeyContent}
        </div>
      </div>
    );
  }

  // ————— Étape d'authentification (login/register) —————
  const authContent = (
    <div className="w-full max-w-md">
      {notification.message && (
        <div
          className={`mb-6 p-4 rounded-xl border backdrop-blur-sm transition-all duration-300 ${notification.type === "success"
            ? "bg-green-500/10 border-green-500/20 text-green-400"
            : "bg-red-500/10 border-red-500/20 text-red-400"
            }`}
        >
          <div className="flex items-center gap-3">
            {notification.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {isLoginMode ? <LogIn className="w-8 h-8 text-white" /> : <UserPlus className="w-8 h-8 text-white" />}
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{isLoginMode ? "Connexion" : "Créer un compte"}</h1>
          <p className="text-gray-400 text-sm">
            {isLoginMode ? "Connectez-vous à votre compte" : "Créez votre nouveau compte"}
          </p>
        </div>

        {/* —— Formulaire Auth —— */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isLoading) return;
            isLoginMode ? handleLogin() : handleRegister();
          }}
          className="space-y-6"
        >
          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="votre@email.com"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Nom d'utilisateur (register uniquement) */}
          {!isLoginMode && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Nom d'utilisateur
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange("username", e.target.value)}
                placeholder="Entrez votre nom d'utilisateur"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          )}

          {/* Mot de passe */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                autoComplete={isLoginMode ? "current-password" : "new-password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Entrez votre mot de passe"
                className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* reCAPTCHA v3 invisible + actions */}
          <div className="mt-8 space-y-3">

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {isLoginMode ? "Connexion..." : "Création..."}
                </>
              ) : (
                <>
                  {isLoginMode ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                  {isLoginMode ? "Se connecter" : "Créer le compte"}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setIsLoginMode(!isLoginMode)}
              className="w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              {isLoginMode ? "Créer un nouveau compte" : "J'ai déjà un compte"}
            </button>
            <p className="text-[10px] text-gray-500 mt-4 text-center">
              Ce site est protégé par reCAPTCHA et les
              <a href="https://policies.google.com/privacy" className="underline ml-1">règles de confidentialité</a> et
              <a href="https://policies.google.com/terms" className="underline ml-1">conditions d'utilisation</a> de Google s'appliquent.
            </p>
          </div>

          {/* Info de sécurité */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-blue-400 text-sm font-medium">Sécurité</p>
                <p className="text-blue-300/80 text-xs mt-1">Vos informations sont chiffrées et sécurisées.</p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="max-h-screen min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
        <HeaderMobile setShowMenu={setShowMenu} showMenu={showMenu} setShowConfirm={setShowConfirm} />
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto">{authContent}</div>
        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full text-center">
              <h2 className="text-xl font-bold text-red-600 mb-4">Confirmation</h2>
              <p className="text-sm text-gray-700 mb-6">
                Cette action est irréversible et supprimera la partie en cours. Voulez-vous vraiment commencer une
                nouvelle partie ?
              </p>
              <div className="flex justify-center space-x-4">
                <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded bg-gray-500 hover:bg-gray-300">
                  Annuler
                </button>
                <button onClick={newGame} disabled={isLoading} className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                  {isLoading ? "Chargement..." : "Confirmer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <Header gameState={gameState} refreshGameState={refreshGameState} />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        {authContent}
      </div>
    </div>
  );
}

export default LoginPage;
